import type { CustomerBooking } from "@/services/naaviApi/types";

type Appointment = {
  id: number;
  doctor: Record<string, unknown>;
};

export function mapCustomerBookingsToAppointments(
  bookings: CustomerBooking[] | undefined | null,
): Appointment[] {
  if (!bookings || bookings.length === 0) {
    return [];
  }

  return bookings.map(mapCustomerBooking);
}

function mapCustomerBooking(booking: CustomerBooking): Appointment {
  const paymentMethods =
    Array.isArray(booking.paymentMethods) && booking.paymentMethods.length > 0
      ? booking.paymentMethods.join(", ")
      : "";

  const services = Array.isArray(booking.professionalServices)
    ? booking.professionalServices
    : [];
  const about = Array.isArray(booking.professionalAbout)
    ? booking.professionalAbout
    : [];
  const education = Array.isArray(booking.professionalEducation)
    ? booking.professionalEducation
    : [];

  const displayPrice = computePriceDisplay(booking);
  const allowRemote =
    typeof booking.allowRemote === "boolean" ? booking.allowRemote : undefined;

  const doctor: Record<string, unknown> = {
    id: booking.resourceId ?? booking.bookingId,
    resourceId: booking.resourceId,
    bookingId: booking.bookingId,
    name: booking.professionalName,
    specialty: booking.professionalSpecialty,
    image: booking.professionalAvatarUrl,
    appointmentDate: booking.startLocalDisplay,
    appointmentStart: booking.start,
    appointmentEnd: booking.end,
    mainAddress: booking.fullAddress || booking.locationName,
    locationName: booking.locationName,
    locationType: booking.locationType,
    allowRemote,
    paymentMethod: paymentMethods,
    services,
    aboutMe: about,
    education,
    consultationFee: booking.totalAmount ?? null,
    totalAmount: booking.totalAmount,
    amountPaid: booking.amountPaid,
    priceStatus: booking.priceStatus,
    priceDisplay: displayPrice,
    canCancel: booking.canCancel,
    cancelDeadline: booking.cancelDeadline,
    isReviewable: booking.isReviewable ?? false,
    reviewDeadline: booking.reviewDeadline ?? undefined,
    tenantId: booking.tenantId,
    tenantXId: booking.tenantId,
    tenantName: booking.tenantName,
    tenantLogo: booking.tenantLogo,
    tenantUrl: booking.tenantUrl ?? null,
    tenantCustomerId:
      typeof booking.customerId === "number" ? booking.customerId : null,
    position:
      booking.locationLatitude != null && booking.locationLongitude != null
        ? { lat: booking.locationLatitude, lng: booking.locationLongitude }
        : null,
  };

  return {
    id: booking.bookingId,
    doctor,
  };
}

function computePriceDisplay(booking: CustomerBooking): string {
  const backendDisplay = booking.priceDisplay?.trim();
  if (backendDisplay) {
    return backendDisplay;
  }

  if (booking.totalAmount == null) {
    return "";
  }

  const numericAmount = Number(booking.totalAmount);
  if (Number.isNaN(numericAmount)) {
    return "";
  }

  const fixed = numericAmount.toFixed(2);
  const symbol = booking.currencySymbol?.trim();
  return symbol ? `${symbol} ${fixed}` : fixed;
}
